import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class PersistenceService {
  private readonly PERSISTENCE_PREFIX = 'simply_';

  private getPrefixedKey(key: string): string {
    return `${this.PERSISTENCE_PREFIX}${key}`;
  }

  private safeExecute<T>(action: () => T, errorMessage: string): T | null {
    try {
      return action();
    } catch (error) {
      console.error(errorMessage, error);
      return null;
    }
  }

  set<T>(key: string, value: T): void {
    const storageKey = this.getPrefixedKey(key);
    this.safeExecute(() => {
      const data = JSON.stringify({ data: value, createdAt: new Date().getTime() });
      localStorage.setItem(storageKey, data);
    }, `Error saving key "${storageKey}" to localStorage`);
  }

  get<T>(key: string): T | null {
    const storageKey = this.getPrefixedKey(key);
    return this.safeExecute(() => {
      const rawData = localStorage.getItem(storageKey);
      if (!rawData) return null;
      const parsedData = JSON.parse(rawData);
      return parsedData?.data ?? null;
    }, `Error retrieving key "${storageKey}" from localStorage`);
  }

  remove(key: string): void {
    const storageKey = this.getPrefixedKey(key);
    this.safeExecute(() => localStorage.removeItem(storageKey), `Error removing key "${storageKey}" from localStorage`);
  }

  clear(): void {
    const keysToRemove = Object.keys(localStorage).filter((key) => key.startsWith(this.PERSISTENCE_PREFIX));
    keysToRemove.forEach((key) => this.safeExecute(() => localStorage.removeItem(key), `Error clearing key "${key}"`));
  }
}
